import { Box } from "@mui/material";
import { useVirtualizer } from "@tanstack/react-virtual";
import { type FC, useCallback, useLayoutEffect, useRef, useState } from "react";
import { ActivityGroupRow } from "web_myplann/Activities/Components/ActivityGroupRow";
import { ActivityGroupRows } from "web_myplann/Activities/Components/ActivityGroupRows";
import {
	ACTIVITY_ROW_ESTIMATED_HEIGHT,
	ACTIVITY_ROW_OVERSCAN,
} from "web_myplann/Activities/constants";
import type { ActivityOrTaskListItem } from "web_myplann/Activities/Helpers/isActivityListItem";
import { getScrollContainer } from "web_myplann/Shared/Utils/getScrollContainer";

type Props = {
	items: ActivityOrTaskListItem[];
	showPerson: boolean;
};

export const ActivityGroupVirtualRows: FC<Props> = ({ items, showPerson }) => {
	const listRef = useRef<HTMLDivElement>(null);
	const [scrollElement, setScrollElement] = useState<HTMLElement | null>(null);
	const [scrollMargin, setScrollMargin] = useState(0);

	const measureScrollMargin = useCallback((scroller: HTMLElement) => {
		const list = listRef.current;
		if (!list) return;

		const offset =
			list.getBoundingClientRect().top -
			scroller.getBoundingClientRect().top +
			scroller.scrollTop;

		setScrollMargin((prev) => (prev === offset ? prev : offset));
	}, []);

	useLayoutEffect(() => {
		const list = listRef.current;
		const scroller = getScrollContainer(list);
		if (!list || !scroller) return;

		setScrollElement(scroller);
		measureScrollMargin(scroller);

		const observer = new ResizeObserver(() => measureScrollMargin(scroller));
		observer.observe(scroller);
		observer.observe(list);

		const observeScrollerChildren = () => {
			for (const child of Array.from(scroller.children)) {
				observer.observe(child);
			}
		};
		observeScrollerChildren();

		const mutationObserver = new MutationObserver(() => {
			observeScrollerChildren();
			measureScrollMargin(scroller);
		});
		mutationObserver.observe(scroller, { childList: true, subtree: true });

		return () => {
			observer.disconnect();
			mutationObserver.disconnect();
		};
	}, [measureScrollMargin]);

	const virtualizer = useVirtualizer({
		count: items.length,
		getScrollElement: () => scrollElement,
		estimateSize: () => ACTIVITY_ROW_ESTIMATED_HEIGHT,
		overscan: ACTIVITY_ROW_OVERSCAN,
		scrollMargin,
	});

	if (!scrollElement) {
		return (
			<Box ref={listRef}>
				<ActivityGroupRows items={items} showPerson={showPerson} />
			</Box>
		);
	}

	return (
		<Box ref={listRef} position="relative" height={virtualizer.getTotalSize()}>
			{virtualizer.getVirtualItems().map((virtualRow) => (
				<Box
					key={virtualRow.key}
					ref={virtualizer.measureElement}
					data-index={virtualRow.index}
					position="absolute"
					top={0}
					left={0}
					width="100%"
					sx={{
						transform: `translateY(${virtualRow.start - scrollMargin}px)`,
					}}
				>
					<ActivityGroupRow
						item={items[virtualRow.index]}
						showDivider={virtualRow.index < items.length - 1}
						showPerson={showPerson}
					/>
				</Box>
			))}
		</Box>
	);
};
